import React from 'react';
import { useTheme } from '@mui/material';
import { ChartOptions } from 'chart.js';
import { format } from 'date-fns';
import { ReviewTrendsData } from './useReivewTrendsQueries';
import { ChartColors } from 'ui/analytics/consts';

export type ReviewsTimeGranularity = 'day' | 'week' | 'month';

interface ReviewsLineChartInput {
    volumeData?: ReviewTrendsData;
    granularity?: ReviewsTimeGranularity;
}

const RATING_COLORS: Record<number, string> = {
    5: ChartColors.GREEN,
    4: ChartColors.BLUE,
    3: ChartColors.ORANGE,
    2: ChartColors.YELLOW,
    1: ChartColors.RED
};

function getDateFormat(granularity: ReviewsTimeGranularity) {
    switch (granularity) {
        case 'month':
            return 'MMM yyyy';
        case 'week':
            return 'd MMM';
        default:
            return 'd MMM';
    }
}

export function useReviewsLineChartData({ volumeData, granularity }: ReviewsLineChartInput) {
    const theme = useTheme();

    const dateFormat = getDateFormat(granularity ?? 'day');

    const chartData = React.useMemo(
        () => ({
            labels: volumeData?.dates.map(d => format(d, dateFormat)) ?? [],
            datasets: [5, 4, 3, 2, 1].map(rating => ({
                id: `rating-${rating}`,
                label: rating === 1 ? `${rating} Star` : `${rating} Stars`,
                data: volumeData?.distribution[rating]?.map(d => d.count) ?? [],
                borderColor: RATING_COLORS[rating],
                backgroundColor: RATING_COLORS[rating],
                tension: 0.3,
                pointRadius: 3,
                pointHoverRadius: 8,
                pointHoverBorderColor: theme.palette.common.white,
                pointHoverBorderWidth: 2,
                borderWidth: 2
            }))
        }),
        [volumeData, dateFormat, theme]
    );

    const chartOptions = React.useMemo<ChartOptions<'line'>>(() => {
        const maxValue = chartData.datasets.reduce((max, ds) => Math.max(max, ...ds.data.map(Number)), 0);
        const stepSize = Math.max(1, Math.pow(10, Math.floor(Math.log10(maxValue || 1))));
        const dates = volumeData?.dates ?? [];

        return {
            scales: {
                y: {
                    ticks: { stepSize }
                }
            },
            plugins: {
                tooltip: {
                    callbacks: {
                        title: items => {
                            const index = items[0]?.dataIndex;
                            if (index !== undefined && dates[index]) {
                                return format(dates[index], 'dd/MM/yyyy');
                            }
                            return items[0]?.label ?? '';
                        }
                    }
                }
            }
        };
    }, [chartData, volumeData?.dates]);

    return { chartData, chartOptions };
}
